import { FC, useMemo } from 'react'
import styles from 'components/features/coaching/advisorPublicPage/AdvisorRatingBlock/AdvisorRatingBlock.styl'
import { cssVar } from 'helpers/styleHelpers'
import { StarsRow } from 'components/features/coaching/advisorPublicPage/StarsRow/StarsRow'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { AdvisorUid } from 'components/features/coaching/advisor/types/advisor'
import SvgIcon from 'components/atoms/SvgIcon'
import StarSvg from 'icons/star.svg'
import { ModulePreset } from 'components/features/coaching/advisorPublicPage/ModulePreset/ModulePreset'

const makePercent = (sum: number) => (count: number) => {
  return count / sum * 100
}

type AdvisorRatingBlock = {
  uid: AdvisorUid
}
export const AdvisorRatingBlock: FC<AdvisorRatingBlock> = (props) => {
  const { uid } = props
  const { ratingBreakdown, rating } = useStoreSelector((state) => {
    const { advisors } = state.data
    return {
      ratingBreakdown: advisors.ratingBreakdown[uid] ?? null,
      rating: advisors.advisors.byKey[uid]?.rating ?? null,
    }
  })

  const details = useMemo(() => {
    if (!ratingBreakdown) {
      return null
    }

    const sumLocal = Object.entries(ratingBreakdown).reduce((acc, [, count]) => (acc + (count ?? 0)), 0)
    if (sumLocal === 0) {
      return null
    }

    const getPercent = makePercent(sumLocal)
    let sumPercents = 0

    const ratingByStarsLocal = [0, 0, 0, 0, 0, 0]
    for (let i = 1; i <= 5; i++) {
      const percent = Math.round(getPercent(ratingBreakdown[i] ?? 0))
      ratingByStarsLocal[i] = percent
      sumPercents += percent
    }

    // fix the error due to loss of precision due to rounding
    const percentsDelta = 100 - sumPercents
    if (percentsDelta > 0) {
      const { index: biggestIndex } = ratingByStarsLocal.reduce(({ index, value }, nextValue, currentIndex) => {
        if (value < nextValue) {
          return {
            index: currentIndex,
            value: nextValue,
          }
        }

        return { index, value }
      }, { index: 0, value: 0 })

      ratingByStarsLocal[biggestIndex] += percentsDelta
    }

    return {
      sum: sumLocal,
      ratingByStars: ratingByStarsLocal,
    }
  }, [ratingBreakdown])

  if (!ratingBreakdown || rating === null || !details || details.sum < 3) {
    return null
  }


  const { sum, ratingByStars } = details

  return (
    <ModulePreset className={ styles.modulePreset } title='Rating'>
      <div className={ styles.advisorRatingBlock }>
        <div className={ styles.left }>
          <div className={ styles.counter }>
            { (+rating).toFixed(1) }
          </div>
          <div className={ styles.stars }>
            <StarsRow rating={ +rating } />
          </div>
          <div className={ styles.reviewsCounter }>
            { sum } reviews
          </div>

        </div>
        <div className={ styles.right }>
          <StarRow starNo={ 5 } ratio={ ratingByStars[5] } />
          <StarRow starNo={ 4 } ratio={ ratingByStars[4] } />
          <StarRow starNo={ 3 } ratio={ ratingByStars[3] } />
          <StarRow starNo={ 2 } ratio={ ratingByStars[2] } />
          <StarRow starNo={ 1 } ratio={ ratingByStars[1] } />
        </div>
      </div>
    </ModulePreset>
  )
}

type StarRowProps = {
  starNo: number
  ratio: number
}
const StarRow: FC<StarRowProps> = (props) => {
  const { starNo, ratio } = props
  return (
    <div className={ styles.starNo }>
      <div className={ styles.starNoTitle }>{ starNo }</div>
      <SvgIcon className={ styles.star } src={ StarSvg } />
      <div
        className={ styles.ratioLine }
        style={ cssVar({
          '--ratio': `${ ratio }%`,
        }) }
      />
      <div className={ styles.ratioTitle }>{ Math.round(ratio) }%</div>
    </div>
  )
}
